/* Native scrolling keeps touch swipes and vertical page scrolling independent. */
if (!customElements.get('rs2-card-gallery')) {
  function modelChoice(cards, target) {
    let balance = cards.reduce((sum, card) => sum + (card.dual ? 0 : card.gender === 'male' ? 1 : -1), 0);
    for (let index = 0; index <= target; index++) {
      const card = cards[index];
      const gender = card.dual ? (balance > 0 ? 'female' : 'male') : card.gender;
      if (index === target) return gender;
      if (card.dual) balance += gender === 'male' ? 1 : -1;
    }
  }
  customElements.define('rs2-card-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const track = this.querySelector('.rs2-card-gallery__track');
      const slides = [...this.querySelectorAll('.rs2-card-gallery__slide')];
      if (!track || !slides.length) return;
      if (this.hasAttribute('data-dual-model')) {
        const group = this.closest('.product-grid, .rs2-related-grid, slider-component') || document;
        const cards = [...group.querySelectorAll('rs2-card-gallery[data-model-gender]')];
        const gender = modelChoice(cards.map(card => ({gender: card.dataset.modelGender, dual: card.hasAttribute('data-dual-model')})), cards.indexOf(this));
        const image = this.querySelector(`[data-model-choice="${gender}"]`)?.content.querySelector('img');
        const current = this.querySelector('[data-model-preview] img');
        if (image && current) { current.replaceWith(image.cloneNode(true)); this.dataset.modelGender = gender; }
      }
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const indicators = [...this.querySelectorAll('.rs2-card-gallery__indicators span')];
      const detail = this.querySelector('.rs2-card-gallery__detail');
      const status = this.querySelector('.rs2-card-gallery__status');
      const detailIndex = Math.min(Number(this.dataset.detailIndex) || 1, slides.length - 1);
      const preview = this.querySelector('.rs2-card-gallery__hover-detail');
      const hoverRoot = this.closest('.product-card, .card-wrapper') || this;
      let current = 0, drag = null, dragged = false, hovered = false;
      let hoverSequence = 0, changeSequence = 0, changing = false, queued = null, transitionImage = null;
      let stopHover = () => {}, stopChange = () => {};
      const updateHover = async () => {
        if (!preview) return;
        const wanted = hovered && !drag && !changing && !this.matches(':focus-within');
        const ticket = ++hoverSequence;
        const level = preview.classList.contains('rs2-detail-visible') ? (stopHover.level?.() ?? 1) : 0;
        stopHover();
        if (wanted) {
          preview.loading = 'eager';
          try { await preview.decode(); } catch (_) { return; }
          if (ticket !== hoverSequence || !this.isConnected) return;
          preview.classList.add('rs2-detail-visible');
        } else if (level === 0) {
          preview.classList.remove('rs2-detail-visible');
          return;
        }
        const done = () => { if (!wanted) preview.classList.remove('rs2-detail-visible'); };
        stopHover = window.rs2DitherFade?.(preview, level, wanted ? 1 : 0, 420, done) || (() => {});
        if (!window.rs2DitherFade) done();
      };
      hoverRoot.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; updateHover(); } }, options);
      hoverRoot.addEventListener('pointerleave', () => { hovered = false; updateHover(); }, options);
      this.addEventListener('focusin', updateHover, options);
      this.addEventListener('focusout', () => queueMicrotask(updateHover), options);
      const update = () => {
        const width = track.clientWidth;
        if (!width) return;
        current = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / width)));
        slides.forEach((slide, index) => { slide.tabIndex = index === current ? 0 : -1; });
        indicators.forEach((indicator, index) => indicator.classList.toggle('is-active', index === current));
        detail?.setAttribute('aria-pressed', String(current === detailIndex));
        const label = `${current + 1} / ${slides.length}`;
        if (status && status.textContent !== label) status.textContent = label;
      };
      const show = async index => {
        index = Math.max(0, Math.min(slides.length - 1, index));
        if (changing) { queued = index; return; }
        if (index === current) return;
        const ticket = ++changeSequence, image = slides[index].querySelector('img');
        if (image) {
          image.loading = 'eager';
          try { await image.decode(); } catch (_) { return; }
        }
        if (ticket !== changeSequence || !this.isConnected) return;
        changing = true;
        const previous = preview?.classList.contains('rs2-detail-visible') ? preview : slides[current].querySelector('img');
        transitionImage = previous?.cloneNode();
        if (transitionImage) {
          transitionImage.removeAttribute('style');
          transitionImage.className = 'rs2-card-gallery__transition';
          transitionImage.alt = '';
          transitionImage.setAttribute('aria-hidden', 'true');
          this.append(transitionImage);
        }
        ++hoverSequence; stopHover(); preview?.classList.remove('rs2-detail-visible');
        track.scrollTo({left: index * track.clientWidth, behavior: 'instant'});
        update();
        const done = () => {
          transitionImage?.remove(); transitionImage = null;
          changing = false; stopChange = () => {};
          const next = queued; queued = null;
          if (next !== null) show(next);
          else updateHover();
        };
        if (transitionImage && window.rs2DitherFade) stopChange = window.rs2DitherFade(transitionImage, 1, 0, 420, done);
        else done();
      };
      this.stopEffects = () => {
        ++hoverSequence; ++changeSequence; hovered = false; queued = null; changing = false;
        stopHover(); stopChange(); transitionImage?.remove(); transitionImage = null;
        preview?.classList.remove('rs2-detail-visible');
      };
      window.addEventListener('pagehide', this.stopEffects, options);
      track.addEventListener('scroll', update, { ...options, passive: true });
      track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        show(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      detail?.addEventListener('click', () => show(current === detailIndex ? 0 : detailIndex), options);
      // Mouse dragging supplements the browser's native touch/trackpad scrolling.
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragged = false;
        this.stopEffects();
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft };
      }, options);
      track.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        if (event.buttons !== 1) { drag = null; track.classList.remove('is-dragging'); return; }
        const delta = event.clientX - drag.x;
        if (!dragged && Math.abs(delta) < 6) return;
        dragged = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
        track.scrollLeft = drag.left - delta;
        event.preventDefault();
      }, options);
      const finishDrag = () => {
        if (!drag) return;
        drag = null;
        track.classList.remove('is-dragging');
        if (dragged) show(Math.round(track.scrollLeft / track.clientWidth));
      };
      track.addEventListener('pointerup', finishDrag, options);
      track.addEventListener('pointercancel', finishDrag, options);
      track.addEventListener('lostpointercapture', finishDrag, options);
      track.addEventListener('dragstart', (event) => event.preventDefault(), options);
      track.addEventListener('click', (event) => {
        if (!dragged) return;
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
      }, { ...options, capture: true });
      this.resizeObserver = new ResizeObserver(() => {
        track.scrollTo({ left: current * track.clientWidth, behavior: 'instant' });
        update();
      });
      this.resizeObserver.observe(track);
      if (detail) detail.hidden = false;
      update();
    }
    disconnectedCallback() {
      this.stopEffects?.();
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
    }
  });
}
